const { useState } = React;

// ISS-19: shown when a job/review route has no live job (deep-link, a build not
// in MAV, or pre-hydration) — instead of passing undefined to JobDetail/Review,
// which deref job.client and white-screen the whole SPA (there's no error boundary).
function BuildNotFound({ go }) {
  return (
    <div className="card card-pad" style={{ margin: 24, textAlign: 'center' }}>
      <h2 style={{ marginBottom: 8 }}>Build not found</h2>
      <p className="muted" style={{ marginBottom: 16 }}>This build isn't in your workspace, or hasn't loaded yet.</p>
      <button className="btn btn-primary" onClick={() => go('builds')}>Back to Builds</button>
    </div>
  );
}

// RBAC-07: UX redirect for a role that can't see admin/usage. The SERVER already
// 403s the underlying /api/admin/* + /api/org/* calls — this just avoids a blank.
function NotAuthorized({ go }) {
  return (
    <div className="card card-pad" style={{ margin: 24, textAlign: 'center' }}>
      <h2 style={{ marginBottom: 8 }}>Not authorized</h2>
      <p className="muted" style={{ marginBottom: 16 }}>This area is for admins and producers. Your role doesn't have access.</p>
      <button className="btn btn-primary" onClick={() => go('home')}>Back to Dashboard</button>
    </div>
  );
}

function App() {
  // VITE_MOCK_AUTH=1 (dev-only offline preview) → start on the dashboard instead of
  // login, so the whole UI renders on the window.MAV mock fixtures with no backend
  // and no login flash. Default → 'login' (the real auth flow).
  const [route, setRoute] = useState((import.meta.env && import.meta.env.VITE_MOCK_AUTH === '1') ? 'home' : 'login');     // login|home|builds|new|running|job|review|kb|usage|settings|members
  const [jobId, setJobId] = useState(null);   // ISS-19: no hardcoded mock id
  const [openedJob, setOpenedJob] = useState(null);
  const [origin, setOrigin] = useState('home');     // where to return to from job/review
  const [navOpen, setNavOpen] = useState(false);    // mobile sidebar drawer
  // RBAC-07: re-render the tree when the viewer/context changes (a workspace switch
  // or an F2 mid-session revocation) so the nav, gates, and build lists react.
  const [, _rbacTick] = useState(0);
  React.useEffect(() => {
    const h = () => _rbacTick((n) => n + 1);
    window.addEventListener('goose-rbac-change', h);
    return () => window.removeEventListener('goose-rbac-change', h);
  }, []);

  // Returning visitor: if a valid session cookie exists, skip login and load
  // live data into MAV before showing the dashboard. No session → stay on login.
  React.useEffect(() => {
    // Dev-only offline preview: VITE_MOCK_AUTH=1 skips the /api/auth/me probe (we're
    // already on 'home' from the initial route) so the WHOLE UI renders on the
    // window.MAV mock fixtures with no backend — no HTTP 500. NEVER set this in a
    // real deploy; the server still gates every data route regardless of the UI.
    if (import.meta.env && import.meta.env.VITE_MOCK_AUTH === '1') return;
    let alive = true;
    (async () => {
      try {
        if (!window.GooseAPI) return;
        const loaded = await window.GooseAuthenticate();
        if (alive && loaded.ok) setRoute('home');
      } catch (_) { /* no session — stay on login */ }
    })();
    return () => { alive = false; };
  }, []);

  // A limited/reordered list refresh must not unmount an already-open editor
  // and discard its local draft. Its actual editor API still owns access/head.
  const job = window.MAV.jobs.find(j => j.id === jobId) || (openedJob && openedJob.context === window.MAV.__contextKey && openedJob.job?.id === jobId ? openedJob.job : null);
  // "running" route reuses JobDetail with the live job — derived from live data
  // (first running build), not a hardcoded mock id that vanishes post-hydrate.
  const runningJob = window.MAV.jobs.find(j => j.status === 'running');

  const [kbSlug, setKbSlug] = useState(null);
  const mayNavigate = () => window.GooseReviewBlocks.requestNavigation(window, window.MAV.__contextKey);
  const openClient = (slug) => { if (!mayNavigate()) return false;setKbSlug(slug);setRoute('kb');return true; };
  // Issues live inside AdminDesk (already RBAC-gated at the route below); search deep-links
  // one by seeding its tab state. Search only offers issues to viewers RBAC says are admin.
  const [adminIssue, setAdminIssue] = useState(null);
  const previousContext = React.useRef(window.MAV.__contextKey);
  React.useLayoutEffect(() => {
    const context = window.MAV.__contextKey;
    if (previousContext.current !== context) {
      previousContext.current = context;
      setKbSlug(null); setAdminIssue(null); setJobId(null); setOpenedJob(null);
      if (context && route !== 'login') setRoute('home');
    }
  }, [window.MAV.__contextKey]);
  const openIssue = (id) => { if (!mayNavigate()) return false;setAdminIssue(id);setRoute('admin');return true; };
  const go = (r) => { if (r !== route && r !== 'login' && !mayNavigate()) return false;setKbSlug(null);setAdminIssue(null);setRoute(r);setNavOpen(false);return true; };
  const openJob = (id, sub) => { if (!mayNavigate()) return false;setOrigin(route === 'login' ? 'home' : route);setJobId(id);setOpenedJob({ context: window.MAV.__contextKey, job: window.MAV.jobs.find(j => j.id === id) });setRoute(sub === 'review' ? 'review' : 'job');return true; };
  const openReview = () => { setOrigin('job'); setRoute('review'); };
  // Global search can land directly on a client profile. KBClients owns its open-profile
  // state internally, so we pass a target and remount it via key rather than reach inside.
  const back = () => go(origin);

  // the job a detail/breadcrumb should reflect (running route shows the live job)
  const currentJob = route === 'running' ? runningJob : job;

  const crumbsFor = () => {
    const jn = (n) => n ? (n.length > 24 ? n.slice(0, 24) + '…' : n) : 'Job';
    const D = { t: 'Dashboard', r: 'home' };   // clickable root → home, present on every non-home page
    switch (route) {
      case 'home': return [{ t: 'Dashboard', b: true }];
      case 'builds': return [D, { t: 'Builds', b: true }];
      case 'new': return [D, { t: 'Builds', r: 'builds' }, { t: 'New build', b: true }];
      case 'running':
      case 'job': return [D, { t: 'Builds', r: 'builds' }, { t: jn(currentJob && currentJob.name), b: true }];
      case 'review': return [
        D,
        { t: 'Builds', r: 'builds' },
        { t: jn(currentJob && currentJob.name), r: 'job' },
        { t: 'Review', b: true },
      ];
      case 'queue': return [D, { t: 'Review queue', b: true }];
      case 'kb': return [D, { t: 'Knowledge base' }, { t: 'Clients', b: true }];
      case 'profilesrc': return [D, { t: 'Knowledge base' }, { t: 'Profile sources', b: true }];
      case 'variants': return [D, { t: 'Knowledge base' }, { t: 'Variants', b: true }];
      case 'usage': return [D, { t: 'Usage & budgets', b: true }];
      case 'admin': return [D, { t: 'Admin desk', b: true }];
      case 'settings': return [D, { t: 'Settings', b: true }];
      case 'profile': return [D, { t: 'Account', b: true }];
      case 'm_builds': return [D, { t: 'Builds this month', b: true }];
      case 'm_pass': return [D, { t: 'Pass rate', b: true }];
      case 'm_time': return [D, { t: 'Avg build time', b: true }];
      case 'm_spend': return [D, { t: 'Token spend', b: true }];
      default: return [{ t: 'Dashboard', b: true }];
    }
  };

  // Login is a full-screen surface with no app chrome
  if (route === 'login') return <Login onAuth={() => go('home')} />;

  // Unmount private screens during a context transition. Their component-local
  // drafts/caches must not survive into a different account or workspace.
  const realAuth = import.meta.env?.VITE_MOCK_AUTH !== '1';
  const loadState = window.MAV.contextLoad;
  if (realAuth && loadState?.status !== 'ready') return (
    <div className="card card-pad" style={{ margin: 24 }} role="status">
      <h2>{loadState?.status === 'loading' ? 'Loading workspace…' : 'Workspace data unavailable'}</h2>
      <p>No previous account or sample data is shown.</p>
      {loadState?.errors?.logout && <p role="alert">Sign-out failed. The server session may still be active; retry signing out.</p>}
      {loadState?.errors?.logout && <button onClick={() => window.GooseSignOut().then(result => { if (result.ok) go('login'); }).catch(() => {})}>Retry sign out</button>}
      {loadState?.status !== 'loading' && <button className="btn btn-primary" onClick={() => window.GooseLoadContext()}>Retry</button>}
      <button className="btn btn-quiet" onClick={() => go('login')}>Back to sign in</button>
    </div>
  );

  let view;
  // RBAC-07: gate admin/usage/members (UX redirect; the server stays authoritative).
  const _rbacView = (window.RBAC && window.RBAC.viewer && window.RBAC.viewer()) || null;
  if (route === 'home') view = <Dashboard go={go} openJob={openJob} />;
  else if (route === 'builds') view = <BuildsList openJob={openJob} />;
  else if (route === 'new') view = <NewBuild go={go} openJob={openJob} />;
  else if (route === 'running') view = runningJob ? <JobDetail key={window.MAV.__contextKey + ':' + runningJob.id} job={runningJob} go={go} back={back} openReview={openReview} openJob={openJob} /> : <BuildNotFound go={go} />;
  else if (route === 'job') view = job ? <JobDetail key={window.MAV.__contextKey + ':' + job.id} job={job} go={go} back={back} openReview={openReview} openJob={openJob} /> : <BuildNotFound go={go} />;
  else if (route === 'review') view = job ? <div className="gx-embed"><ReviewWorkspace job={job} go={go} back={back} openJob={openJob} /></div> : <BuildNotFound go={go} />;
  else if (route === 'queue') view = <ReviewQueue openJob={openJob} />;
  else if (route === 'kb') view = <KBClients key={kbSlug || 'list'} initialSlug={kbSlug} />;
  else if (route === 'profilesrc') view = <ProfileSources />;
  else if (route === 'variants') view = ((!window.MAV.__live && !_rbacView) || window.RBAC.requireRole('upload', _rbacView))
    ? <window.VariantsDash key={window.MAV.__contextKey} go={go} /> : <NotAuthorized go={go} />;
  else if (route === 'usage') view = (_rbacView && !_rbacView.canSeeAdminDesk) ? <NotAuthorized go={go} /> : <UsageBudgets go={go} />;
  else if (route === 'admin') view = (_rbacView && !_rbacView.canSeeAdminDesk) ? <NotAuthorized go={go} /> : <AdminDesk key={adminIssue || 'desk'} go={go} initialIssue={adminIssue} />;
  else if (route === 'members') view = (_rbacView && !_rbacView.canManageMembers) ? <NotAuthorized go={go} /> : <MembersAdmin />;
  else if (route === 'settings') view = <Settings go={go} />;
  else if (route === 'profile') view = <Profile go={go} />;
  else if (route === 'm_builds') view = <MetricBuilds go={go} openJob={openJob} />;
  else if (route === 'm_pass') view = <MetricPass go={go} openJob={openJob} />;
  else if (route === 'm_time') view = <MetricTime go={go} openJob={openJob} />;
  else if (route === 'm_spend') view = <MetricSpend go={go} openJob={openJob} />;

  // sidebar active state: detail screens belong to the section they came from
  const navRoute = ['m_builds', 'm_pass', 'm_time', 'm_spend'].includes(route) ? 'home'
    : ['running', 'job', 'new'].includes(route) ? 'builds'
    : route === 'review' ? (origin === 'queue' ? 'queue' : 'builds')
    : route;

  return (
    <div className={'app' + (route === 'review' && job ? ' app-review-workspace' : '') + (route === 'variants' ? ' app-variants' : '')} key={window.MAV.__contextKey || 'demo'}>
      <div className={'nav-backdrop' + (navOpen ? ' open' : '')} onClick={() => setNavOpen(false)}></div>
      <Sidebar route={navRoute} go={go} open={navOpen} />
      <div className="main">
        {realAuth && Object.keys(loadState?.errors || {}).length > 0 && <p role="alert" className="card card-pad">Some workspace data is unavailable: {Object.keys(loadState.errors).join(', ')}. <button onClick={() => window.GooseLoadContext()}>Retry</button></p>}
        <Topbar crumbs={crumbsFor()} go={go} route={route} onMenu={() => setNavOpen(true)} openJob={openJob} openClient={openClient} openIssue={openIssue} />
        {view}
      </div>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);
